import {
  type AdjustParams,
  applyFilter,
  neutralParams,
  type PixelBuffer,
  paramsAreNeutral,
} from '../core/filters';
import { getLayer, updateLayer } from '../core/model/document';
import type { AdjustmentKind } from '../core/model/types';
import { decodeLayer, encodeLayer } from '../io/raster';
import { useEditorStore } from '../state/editorStore';
import type { AppContext } from './context';
import { refuseMultiSelection } from './context';
import { type PaintTarget, resolvePaintTarget } from './paintTarget';

/**
 * The adjustment session.
 *
 * A session decodes the target layer **once** and keeps the raw pixels here, out
 * of the store: a slider drag then re-runs pure maths on a copy, which is fast
 * enough to track the pointer. Re-decoding and re-encoding a PNG per frame would
 * not be.
 *
 * Nothing reaches the document until `commitAdjustment`, so a slider dragged
 * anywhere and abandoned costs no history entry, and applying is one entry.
 */

interface Session {
  layerId: string;
  source: PixelBuffer;
  target: PaintTarget;
  /** Document revision the decode happened at, to notice the floor moving. */
  revision: number;
}

let session: Session | null = null;
let watched = false;

export function activeSession(): Session | null {
  return session;
}

/**
 * Drop the session if the document moves underneath it.
 *
 * A preview holds a decode taken at one point in history. Undo, a new file or an
 * open would leave those pixels describing a layer that no longer exists, and
 * applying them would write stale data. Watching the revision in one place is
 * safer than remembering to cancel from every command that can change the doc.
 */
function ensureWatched(): void {
  if (watched) return;
  watched = true;
  useEditorStore.subscribe((state) => {
    if (!state.adjustment || !session) return;
    const layer = getLayer(state.doc, session.layerId);
    if (!layer || state.revision !== session.revision) {
      session = null;
      useEditorStore.getState().setAdjustment(null);
    }
  });
}

/** The layer an adjustment may act on: raster, visible and unlocked. */
export function adjustmentTarget(ctx: AppContext): { target: PaintTarget | null; reason?: string } {
  const { doc, activeLayerId } = ctx.store.getState();
  const resolved = resolvePaintTarget(doc, activeLayerId);
  if (resolved.target) return { target: resolved.target };
  const reasons = {
    'no-layer': 'Adjustments need an image or paint layer.',
    locked: 'That layer is locked.',
    hidden: 'That layer is hidden — show it to adjust it.',
    unsupported: 'That layer type cannot be adjusted.',
  } as const;
  return { target: null, reason: reasons[resolved.reason ?? 'no-layer'] };
}

/** Begin previewing `kind` on the active layer. */
export async function startAdjustment(ctx: AppContext, kind: AdjustmentKind): Promise<void> {
  const { setAdjustment, adjustment, revision } = ctx.store.getState();
  ensureWatched();
  // A preview session has one target; multi-layer adjust is a roadmap note,
  // not a rider on this iteration (design doc §4).
  if (refuseMultiSelection(ctx, 'Adjustments work on one layer — select just it')) return;
  const { target, reason } = adjustmentTarget(ctx);
  if (!target) {
    if (reason) ctx.notify(reason, 'warn');
    return;
  }
  if (adjustment?.layerId === target.layerId && adjustment.kind === kind) return;
  // Switch the panel over immediately so a click is never silently swallowed, then
  // decode. `ready` is what the preview waits for: without it, a slider moved
  // during the decode would preview nothing and never be redrawn.
  setAdjustment({ layerId: target.layerId, kind, params: neutralParams(kind), ready: false });
  if (!session || session.layerId !== target.layerId) {
    session = {
      layerId: target.layerId,
      source: await decodeLayer(target.src, target.width, target.height),
      target,
      revision,
    };
  }
  // Keep whatever the user already dragged while the decode was in flight.
  const current = ctx.store.getState().adjustment;
  if (!current || current.layerId !== target.layerId || current.kind !== kind) return;
  setAdjustment({ ...current, ready: true });
}

/** Filtered pixels for the current preview, without touching the document. */
export function previewPixels(kind: AdjustmentKind, params: AdjustParams): PixelBuffer | null {
  if (!session) return null;
  return applyFilter(
    {
      data: new Uint8ClampedArray(session.source.data),
      width: session.source.width,
      height: session.source.height,
    },
    kind,
    params,
  );
}

/** Bake the preview into the layer as one history entry. */
export function commitAdjustment(ctx: AppContext): void {
  const { adjustment, commit, doc, notify } = ctx.store.getState();
  if (!adjustment || !session) {
    ctx.store.getState().setAdjustment(null);
    return;
  }
  if (paramsAreNeutral(adjustment.kind, adjustment.params)) {
    // Applying nothing should not cost an undo step. Encoding and storing a fresh
    // PNG of identical pixels would add a history entry that undoes to itself.
    cancelAdjustment(ctx);
    const label = adjustment.kind[0]?.toUpperCase() + adjustment.kind.slice(1);
    notify(`${label} would change nothing`);
    return;
  }
  const current = session;
  const result = applyFilter(
    {
      data: new Uint8ClampedArray(current.source.data),
      width: current.source.width,
      height: current.source.height,
    },
    adjustment.kind,
    adjustment.params,
  );
  const pixels = encodeLayer(result);
  const next = updateLayer(doc, current.target.layerId, {
    [current.target.field]: pixels,
  } as never);
  const label = adjustment.kind[0]?.toUpperCase() + adjustment.kind.slice(1);
  // Clear first: committing bumps the revision, and the watcher would otherwise
  // treat our own commit as the floor moving.
  session = null;
  ctx.store.getState().setAdjustment(null);
  commit(label, next);
  notify(`${label} applied`);
}

export function cancelAdjustment(ctx: AppContext): void {
  session = null;
  ctx.store.getState().setAdjustment(null);
}

/** Drop the session when the document moves underneath it (undo, new file…). */
export function invalidateAdjustment(): void {
  session = null;
}
